<script setup>
// M26.2 — roles x capabilities grid, purely derived from the workforce
// registry roster (already fetched) plus live usage counts from
// workforce_analytics#capability_usage where available.
import { computed } from 'vue';

const props = defineProps({
  roles: { type: Array, required: true }, // workforce_registry entries
  capabilityUsage: { type: Object, default: () => ({}) },
});

const capabilities = computed(() => {
  const set = new Set();
  props.roles.forEach(role => role.capabilities?.forEach(c => set.add(c)));
  return [...set].sort();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="rounded-xl border border-n-weak bg-n-background overflow-hidden">
    <div class="px-4 py-3 border-b border-n-weak">
      <h2
        class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider flex items-center gap-1.5"
      >
        <span class="i-lucide-grid-3x3 size-3.5" />
        Capability Matrix
      </h2>
    </div>
    <div class="overflow-x-auto">
      <table class="min-w-full text-xs">
        <thead>
          <tr class="border-b border-n-weak">
            <th
              class="text-left px-4 py-2 font-medium text-n-slate-8 sticky left-0 bg-n-background"
            >
              Role
            </th>
            <th
              v-for="cap in capabilities"
              :key="cap"
              class="px-2 py-2 font-medium text-n-slate-8 capitalize whitespace-nowrap"
            >
              {{ cap.replace(/_/g, ' ') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="role in roles"
            :key="role.id"
            class="border-b border-n-weak last:border-0"
          >
            <td
              class="px-4 py-2 font-medium text-n-slate-11 whitespace-nowrap sticky left-0 bg-n-background"
            >
              {{ role.name }}
            </td>
            <td
              v-for="cap in capabilities"
              :key="cap"
              class="px-2 py-2 text-center"
            >
              <span
                v-if="role.capabilities?.includes(cap)"
                class="inline-flex items-center justify-center size-4 rounded-full bg-woot-100 dark:bg-woot-500/20"
                :title="`${capabilityUsage[cap] || 0} used`"
              >
                <span
                  class="i-lucide-check size-2.5 text-woot-600 dark:text-woot-400"
                />
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
